import React from 'react';
import { SchematicShell, Line } from './SchematicPrimitives';

const IN = 0.0254;
const LEAN = 0.1;
const COS_L = Math.cos(LEAN);
const SIN_L = Math.sin(LEAN);

function tiltY(d) {
  return d * COS_L;
}
function tiltX(d) {
  return d * SIN_L;
}

// ── Stick Vacuum Schematic ──

export function DormStickVacuumSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      >
        <StickVacSide
          depth={depth}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      </SchematicShell>
    );
  }

  const headH = 1.5 * IN;
  const dustR = 1.3 * IN;
  const wandR = 0.45 * IN;
  const handleR = 0.6 * IN;
  const cx = width / 2;

  const headTop = height - headH;
  const bodyH = height - headH;
  const jointLen = 0.8 * IN;
  const dustLen = 5 * IN;
  const ringLen = 0.4 * IN;
  const motorLen = 4 * IN;
  const handleLen = 5 * IN;
  const wandLen = bodyH - jointLen - dustLen - ringLen - motorLen - handleLen;

  const dJoint = jointLen;
  const dDustBot = dJoint;
  const dDustTop = dDustBot + dustLen;
  const dMotorTop = dDustTop + ringLen + motorLen;
  const dWandTop = dMotorTop + wandLen;
  const dHandleTop = dWandTop + handleLen;

  const dustBotY = headTop - tiltY(dDustBot);
  const dustTopY = headTop - tiltY(dDustTop);
  const motorTopY = headTop - tiltY(dMotorTop);
  const wandTopY = headTop - tiltY(dWandTop);
  const handleTopY = headTop - tiltY(dHandleTop);

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {/* Floor head */}
      <Line
        x1={0}
        y1={headTop}
        x2={width}
        y2={headTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Dust cup outline (wider section) */}
      <Line
        x1={cx - dustR}
        y1={dustBotY}
        x2={cx - dustR}
        y2={dustTopY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={cx + dustR}
        y1={dustBotY}
        x2={cx + dustR}
        y2={dustTopY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={cx - dustR}
        y1={dustTopY}
        x2={cx + dustR}
        y2={dustTopY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth * 0.5}
      />
      {/* Taper from motor to wand */}
      <Line
        x1={cx - dustR * 0.88}
        y1={dustTopY}
        x2={cx - wandR * 1.3}
        y2={motorTopY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={cx + dustR * 0.88}
        y1={dustTopY}
        x2={cx + wandR * 1.3}
        y2={motorTopY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Wand */}
      <Line
        x1={cx - wandR}
        y1={motorTopY}
        x2={cx - wandR}
        y2={wandTopY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={cx + wandR}
        y1={motorTopY}
        x2={cx + wandR}
        y2={wandTopY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Handle */}
      <Line
        x1={cx - handleR}
        y1={wandTopY}
        x2={cx - handleR * 0.8}
        y2={handleTopY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={cx + handleR}
        y1={wandTopY}
        x2={cx + handleR * 0.8}
        y2={handleTopY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={cx - handleR * 0.8}
        y1={handleTopY}
        x2={cx + handleR * 0.8}
        y2={handleTopY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </SchematicShell>
  );
}

function StickVacSide({ depth, height, strokeColor, strokeWidth }) {
  const headH = 1.5 * IN;
  const headTop = height - headH;
  const bodyLen = height - headH;
  const cz = depth / 2;
  const dustR = 1.3 * IN;
  const wandR = 0.45 * IN;

  const topZ = cz + tiltX(bodyLen);
  const dustTopD = 0.8 * IN + 5 * IN;
  const dustTopZ = cz + tiltX(dustTopD);
  const motorTopD = dustTopD + 0.4 * IN + 4 * IN;
  const motorTopZ = cz + tiltX(motorTopD);

  return (
    <g>
      {/* Floor head */}
      <Line
        x1={0}
        y1={headTop}
        x2={depth}
        y2={headTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Lean axis — left edge */}
      <Line
        x1={cz - dustR}
        y1={headTop}
        x2={dustTopZ - dustR * 0.88}
        y2={headTop - tiltY(dustTopD)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Lean axis — right edge */}
      <Line
        x1={cz + dustR}
        y1={headTop}
        x2={dustTopZ + dustR * 0.88}
        y2={headTop - tiltY(dustTopD)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Taper to wand */}
      <Line
        x1={dustTopZ - dustR * 0.88}
        y1={headTop - tiltY(dustTopD)}
        x2={motorTopZ - wandR}
        y2={headTop - tiltY(motorTopD)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={dustTopZ + dustR * 0.88}
        y1={headTop - tiltY(dustTopD)}
        x2={motorTopZ + wandR}
        y2={headTop - tiltY(motorTopD)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Wand to top */}
      <Line
        x1={motorTopZ - wandR}
        y1={headTop - tiltY(motorTopD)}
        x2={topZ - wandR}
        y2={headTop - tiltY(bodyLen)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={motorTopZ + wandR}
        y1={headTop - tiltY(motorTopD)}
        x2={topZ + wandR}
        y2={headTop - tiltY(bodyLen)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

DormStickVacuumSchematic.supportsSideViews = true;

// ── Handheld Vacuum Schematic ──

export function DormHandheldVacuumSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      >
        <HandheldVacSide
          depth={depth}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      </SchematicShell>
    );
  }

  const bodyH = height * 0.65;
  const bodyW = width * 0.55;
  const nozzleW = width * 0.45;
  const bodyTop = height - bodyH;

  const bodyLeft = (width - bodyW) / 2;
  const bodyRight = bodyLeft + bodyW;
  const nozzleLeft = bodyLeft - nozzleW;

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {/* Body */}
      <Line
        x1={bodyLeft}
        y1={bodyTop}
        x2={bodyRight}
        y2={bodyTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={bodyLeft}
        y1={bodyTop}
        x2={bodyLeft}
        y2={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={bodyRight}
        y1={bodyTop}
        x2={bodyRight}
        y2={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Nozzle (tapered) */}
      <Line
        x1={nozzleLeft}
        y1={height}
        x2={bodyLeft}
        y2={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={nozzleLeft}
        y1={height}
        x2={nozzleLeft + nozzleW * 0.4}
        y2={bodyTop + bodyH * 0.5}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={nozzleLeft + nozzleW * 0.4}
        y1={bodyTop + bodyH * 0.5}
        x2={bodyLeft}
        y2={bodyTop + bodyH * 0.5}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth * 0.5}
      />
      {/* Handle (angled up-right) */}
      <Line
        x1={bodyRight}
        y1={bodyTop}
        x2={bodyRight + width * 0.15}
        y2={0}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={bodyLeft + bodyW * 0.3}
        y1={bodyTop}
        x2={bodyRight - bodyW * 0.1}
        y2={0}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </SchematicShell>
  );
}

function HandheldVacSide({ depth, height, strokeColor, strokeWidth }) {
  const bodyH = height * 0.65;
  const bodyTop = height - bodyH;

  return (
    <g>
      <Line
        x1={0}
        y1={bodyTop}
        x2={depth}
        y2={bodyTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

DormHandheldVacuumSchematic.supportsSideViews = true;
